import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useTranslation } from "react-i18next";
import CalendarIcon from "../../assets/images/calender-icon.svg";
import StayCalendar from "./StayCalendar";
import { ARRIVAL, DEPARTURE, resolveDayClick } from "../../helper/stayRange";

const toDate = (value) => (value ? new Date(value) : null);

const formatDate = (date) => (date ? moment(date).format("DD.MM.YYYY") : "");

// One calendar for the whole stay: the first click picks the arrival, the
// calendar stays open and the second click picks the departure.
const StayDatePicker = ({
  step,
  arrivalDate,
  departureDate,
  bookedDates = [],
  minimumStayMap = {},
  onChange,
  getDayClassName,
  renderDayContents,
}) => {
  const { t } = useTranslation();
  const [activeField, setActiveField] = useState(null);
  const arrivalRef = useRef(null);
  const departureRef = useRef(null);

  const arrival = toDate(arrivalDate);
  const departure = toDate(departureDate);
  const isOpen = activeField !== null;
  const disabled = step === 2;

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      const isInside = [arrivalRef, departureRef].some((ref) =>
        ref.current?.contains(event.target)
      );
      if (!isInside) setActiveField(null);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setActiveField(null);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (disabled) setActiveField(null);
  }, [disabled]);


  const openField = (field) => {
    if (disabled) return;
    setActiveField(field === DEPARTURE && !arrival ? ARRIVAL : field);
  };

  const handleDayClick = (date) => {
    if (!date) return;

    const result = resolveDayClick({
      activeField,
      date,
      arrivalDate: arrival,
      departureDate: departure,
      bookedDates,
      minimumStayMap,
    });

    onChange({
      arrivalDate: result.arrivalDate,
      departureDate: result.departureDate,
    });
    setActiveField(result.close ? null : result.activeField);
  };

  const clearDates = () => {
    onChange({ arrivalDate: null, departureDate: null });
    setActiveField(ARRIVAL);
  };

  const renderField = (field, label, value, ref) => (
    <li
      ref={ref}
      className={`stay-date-field ${
        activeField === field ? "stay-date-field--active" : ""
      }`}
    >
      <h4 className='text-upper'>{label}</h4>
      <div className='relative-date-picker rental-dp-calender-main'>
        {/* Same wrapper classes as the datepicker input, so it keeps its look. */}
        <div className='react-datepicker-wrapper'>
          <div className='react-datepicker__input-container'>
            <input
              type='text'
              readOnly
              className='formControl stay-date-input'
              placeholder={label}
              value={formatDate(value)}
              disabled={disabled}
              onClick={() => openField(field)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openField(field);
                }
              }}
            />
          </div>
        </div>
        <img src={CalendarIcon} alt='calendar' />
      </div>
      {field === ARRIVAL && isOpen && (
        <div
          className='stay-date-panel'
          onClick={(event) => {
            if (event.target === event.currentTarget) setActiveField(null);
          }}
        >
          <div className='stay-date-panel__content'>
            <div className='stay-date-panel__summary'>
              {[
                [ARRIVAL, t("arrival"), arrival],
                [DEPARTURE, t("departure"), departure],
              ].map(([summaryField, summaryLabel, summaryValue]) => (
                <button
                  key={summaryField}
                  type='button'
                  className={`stay-date-panel__summary-item ${
                    activeField === summaryField
                      ? "stay-date-panel__summary-item--active"
                      : ""
                  }`}
                  onClick={() => openField(summaryField)}
                >
                  <span>{summaryLabel}</span>
                  <strong>{formatDate(summaryValue) || summaryLabel}</strong>
                </button>
              ))}
            </div>
            <StayCalendar
              activeField={activeField}
              arrival={arrival}
              departure={departure}
              bookedDates={bookedDates}
              minimumStayMap={minimumStayMap}
              getDayClassName={getDayClassName}
              renderDayContents={renderDayContents}
              onDayClick={handleDayClick}
              onClear={clearDates}
            />
          </div>
        </div>
      )}
    </li>
  );

  return (
    <>
      {renderField(ARRIVAL, t("arrival"), arrival, arrivalRef)}
      {renderField(DEPARTURE, t("departure"), departure, departureRef)}
    </>
  );
};

export default StayDatePicker;
